import { useRouter } from "next/router"
import { useState, useRef } from "react"
import useOnClickOutside from "@/lib/hooks/useOnClickOutside"
import styles from "./info-panel.module.css"
import Tooltip from "@/common/tooltip/tooltip"
import { Tooltip as Icon } from "@/common/icons/member/tooltip"
import { Edit } from "@/common/icons/member/edit"
import { LinkElem } from "@/blocks/theme/link/link"
import ContactModal from "@/blocks/member/membership/contact-modal"
import classNames from "classnames"

export default function InfoPanel({
  className,
  slug,
  heading,
  value,
  tooltip,
  editText,
  editUrl,
  hasEdit,
  tooltipPosition
}) {
  const router = useRouter()
  const [tooltipOpen, setTooltipOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const tooltipRef = useRef(null)
  useOnClickOutside(tooltipRef, () => setTooltipOpen(!tooltipOpen), tooltipOpen)

  let text
  let type = typeof value
  type = Array.isArray(value) ? "array" : type

  switch (slug) {
    case "address":
      text = [
        value.careOf,
        value.addressRow,
        value.postalCode + " " + value.city
      ]
        .filter(Boolean)
        .join("<br />")
      break

    case "contact":
      text = [value.phone, value.mobilePhone, value.email]
        .filter(Boolean)
        .join("<br />")
      break

    case "organization":
      text = value?.name || "&mdash;"
      if (value.url) {
        text = `<a href="${value.url}" target="_blank">${text}</a>`
      }
      break

    default:
      text = type === "array" ? value.join("<br />") : value
      break
  }

  const isExternal = editUrl?.url.substr(0, 4) === "http" || editUrl?.target

  const handleEditClick = (slug) => {
    if (slug === "contact") {
      setEditOpen(true)
    }
  }

  return (
    <div className={classNames(className, styles.wrapper)}>
      <header ref={tooltipRef}>
        <h3>{heading}</h3>
        {tooltip && (
          <div
            className={classNames("tooltip", tooltipPosition, {
              active: tooltipOpen
            })}
            onClick={(e) => {
              e.preventDefault()
              setTooltipOpen(!tooltipOpen)
            }}>
            <Icon />
            {tooltipPosition === "right" && tooltipOpen && (
              <Tooltip
                title={heading}
                position={tooltipPosition}
                html={tooltip}
                close={(e) => {
                  e.preventDefault()
                  setTooltipOpen(!tooltipOpen)
                }}
              />
            )}
          </div>
        )}
        {tooltipPosition === "under" && tooltipOpen && (
          <Tooltip
            title={heading}
            text={tooltip}
            position={tooltipPosition}
            close={(e) => {
              e.preventDefault()
              setTooltipOpen(!tooltipOpen)
            }}
          />
        )}
        {hasEdit && !editUrl?.url && (
          <button aria-label={editText} onClick={() => handleEditClick(slug)}>
            {editText}
            <Edit className="edit" />
          </button>
        )}
        {hasEdit && editUrl?.url && (
          <LinkElem
            className="edit-link"
            link={editUrl}
            isExternal={isExternal}>
            {editText}
            <Edit className="edit" />
          </LinkElem>
        )}
      </header>
      <p dangerouslySetInnerHTML={{ __html: text || "&mdash;" }} />
      {hasEdit && slug === "contact" && (
        <ContactModal
          contact={value}
          isOpen={editOpen}
          setIsOpen={setEditOpen}
          onSave={(_, response) => {
            if (response?.status) {
              router.reload()
            }
          }}
        />
      )}
    </div>
  )
}
